import type { Company, RolePermissionName } from 'common/api/endpoints/companies';
import type { Onboarding, OnboardingUpdateData } from 'common/api/endpoints/onboarding';
import type { LucideProps } from 'lucide-react';

export enum OnboardingSteps {
  setupGroups = 'setup-groups',
  autoGrouping = 'auto-grouping',
  createFirstIdeas = 'create-first-ideas',
  installFeedbackSources = 'install-feedback-sources',
  importFeedback = 'import-feedback',
  installIntegrations = 'install-integrations',
  enableFeedbackPortal = 'enable-feedback-portal',
  inviteTeam = 'invite-team',
  publicReviews = 'public-reviews',
  createBoard = 'create-board',
  customizePortal = 'customize-portal',
  widget = 'widget',
  syncUserData = 'sync-user-data',
  setupChangelogLabels = 'setup-changelog-labels',
  publishFirstChangelogEntry = 'publish-first-changelog-entry',
}

export enum OnboardingProducts {
  announcements = 'announcements',
  autopilot = 'autopilot',
  feedbackPortal = 'feedback-portal',
  ideas = 'ideas',
}

export type OnboardingValidator = (company: Company, onboarding: Onboarding) => boolean;

export enum OnboardingStepVisibilityBehavior {
  always = 'always',
  overflow = 'overflow',
  conditional = 'conditional',
}

export enum OnboardingStepType {
  button = 'button',
  expandable = 'expandable',
}

export enum OnboardingStepStatus {
  completed = 'completed',
  incomplete = 'incomplete',
}

export enum AsyncOnboardingStepStatus {
  idle = 'idle',
  pending = 'pending',
  completed = 'completed',
  failed = 'failed',
}

type BaseOnboardingStep = {
  condition?: OnboardingValidator;
  description: React.ReactNode;
  icon?: React.ComponentType<LucideProps>;
  id: OnboardingSteps;
  isCompleted: OnboardingValidator;
  isDisabled?: OnboardingValidator;
  overflowDescription?: string;
  overflowTitle?: string;
  requiredPermissions?: RolePermissionName[];
  status: OnboardingStepStatus;
  pollStatus?: (onboarding: Onboarding) => Promise<AsyncOnboardingStepStatus>;
  title: string;
  type: OnboardingStepType;
};

type BaseOnboardingStepWithHref = BaseOnboardingStep & {
  href: string | ((onboarding: Onboarding) => string);
  onClick?: never;
};

type BaseOnboardingStepWithOnClick = BaseOnboardingStep & {
  alertDialog?: (props: {
    onboarding: Onboarding;
    isOpen: boolean;
    isLoading: boolean;
    onOpenChange: (open: boolean) => void;
    onConfirm: () => Promise<void>;
  }) => React.ReactNode;
  href?: never;
  onClick: (onboarding: Onboarding) => void | Promise<{ success: boolean; error?: string }>;
};

export type OnboardingStepBase = BaseOnboardingStepWithHref | BaseOnboardingStepWithOnClick;

type OnboardingStepButton = OnboardingStepBase & {
  type: OnboardingStepType.button;
  useCaseUpdatePayload?: Partial<OnboardingUpdateData>;
  buttonTitle: string;
  buttonDisabled?: OnboardingValidator;
};

type OnboardingStepExpandable = OnboardingStepBase & {
  type: OnboardingStepType.expandable;
  renderContent: () => React.ReactNode;
};

export type OnboardingStep = OnboardingStepButton | OnboardingStepExpandable;

export type OnboardingStepIdWithVisibleBehavior = {
  id: OnboardingSteps;
  visibilityBehavior: OnboardingStepVisibilityBehavior;
};

export type OnboardingProduct = {
  condition?: OnboardingValidator;
  description: string;
  id: OnboardingProducts;
  isDisabled?: OnboardingValidator;
  name: string;
  useCaseSetupPayload: Partial<OnboardingUpdateData>;
  steps: OnboardingStepIdWithVisibleBehavior[];
  introVideo?: {
    title: string;
    description: string;
    url: string;
    condition?: OnboardingValidator;
  };
};
